import { addMinutes, format, parseISO } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { useEffect, useMemo } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import {
  AppointmentResponse,
  MAX_DAYS_TO_DISPLAY,
  MeetingType,
  StaffDetails,
} from "@smart/bridge-types-basic";
import { Icon, Link, Select } from "@smart/itops-sb-design-system-dom";
import { buildTimeZoneList, localTimeZoneCode } from "@smart/itops-utils-basic";

import { getSelectionInfoClassNames } from "./class-names";
import { DatePicker } from "./date-picker";
import { TimePicker } from "./time-picker";
import { AppointmentFieldWrapper } from "./wrapper";
import { fieldName } from "../../../hooks";
import {
  APPT_DEFAULT_DURATION,
  APPT_DEFAULT_DURATION_UNIT,
  SelectedAppointment,
} from "../../../types";
import {
  formatTimeOfDay,
  getDateString,
  timeOfDayInMinutes,
  useAvailableSlots,
} from "../../../utils";
import { ErrorDisplay } from "../error";
import { FieldProps, FieldWrapper } from "../wrapper";

export { AppointmentFieldWrapper };

export const getAppointmentDuration = (
  duration: number,
  durationUnit: string | null | undefined = APPT_DEFAULT_DURATION_UNIT,
) =>
  `${duration} ${duration && duration === 1 ? durationUnit?.slice(0, -1) : durationUnit}`;

export const getAppointmentTimezoneOptions = () =>
  buildTimeZoneList().map((tz) => ({
    ...tz,
    label: tz.label.split("/").pop() || "",
  }));

const informationStyles = "flex flex-1 items-center gap-small";
const rowStyles =
  "flex justify-center text-ui flex-col tablet:flex-row gap-medium";

export const intakeMeetingTypeLabel: Record<MeetingType, string> = {
  inPerson: "In-person meeting",
  telephoneConsult: "Phone meeting",
};

export const AppointmentField = ({
  field: fieldValues,
  value,
  error,
  disabled,
  onBlur,
  onChange,
  index,
  staffDetails,
  loading,
}: FieldProps & { staffDetails: StaffDetails[] }) => {
  const {
    availableStaffIds,
    availability,
    duration,
    durationUnit = APPT_DEFAULT_DURATION_UNIT,
    meetingType,
    timezone: fieldTimeZone,
    minimumNotice,
    bufferTime,
  } = fieldValues;

  const getDurationInMinutes = () => {
    if (!duration) return APPT_DEFAULT_DURATION;
    if (durationUnit === "hours") return duration * 60;
    return duration;
  };
  const id = fieldName({ field: fieldValues, index });
  const errorId = fieldName({ field: fieldValues, index, suffix: "error" });

  const timeZoneOptions = getAppointmentTimezoneOptions();
  const timeZoneOptionsByRegion = timeZoneOptions.reduce(
    (
      groupByRegions: { region: string; items: typeof timeZoneOptions }[],
      option,
    ) => {
      const region = groupByRegions.find((r) => r.region === option.region);
      if (!region) {
        groupByRegions.push({
          region: option.region,
          items: [option],
        });
      } else {
        region.items.push(option);
      }
      return groupByRegions;
    },
    [],
  );
  const localTimeZone = timeZoneOptions.find(
    (tz) => tz.tzCode === localTimeZoneCode,
  );
  const availableStaff = useMemo(
    () => staffDetails.filter((s) => s && availableStaffIds?.includes(s.id)),
    [availableStaffIds, staffDetails],
  );

  const getTimeZone = (ofTimeZoneCode: string | undefined) =>
    timeZoneOptions.find((tz) => tz.tzCode === ofTimeZoneCode);

  const getDefaultValues = (
    response: AppointmentResponse | undefined,
  ): SelectedAppointment => {
    const usedTimeZone = getTimeZone(response?.timezone) || localTimeZone;
    const startTimeAsDate =
      response?.startTime && usedTimeZone
        ? toZonedTime(response.startTime, usedTimeZone.tzCode)
        : null;
    const defaultTeamMember =
      availableStaff.length === 1
        ? {
            id: availableStaff[0].id,
            firstName: availableStaff[0].firstName,
            middleName: availableStaff[0].middleName || undefined,
            lastName: availableStaff[0].lastName || "",
          }
        : null;

    return {
      selectedTeamMember: response?.bookedStaff?.[0] || defaultTeamMember,
      selectedDate: startTimeAsDate && getDateString(startTimeAsDate),
      selectedTime:
        startTimeAsDate && response?.endTime
          ? {
              hour: startTimeAsDate.getHours(),
              minute: startTimeAsDate.getMinutes(),
            }
          : undefined,
      timezone: usedTimeZone,
    };
  };

  const formMethods = useForm<SelectedAppointment>({
    defaultValues: getDefaultValues(value),
  });

  const { control, handleSubmit, watch, setValue } = formMethods;
  const selectedTeamMember = watch("selectedTeamMember");
  const selectedDate = watch("selectedDate");
  const selectedTime = watch("selectedTime");
  const selectedTimezone = watch("timezone");

  const staffBusySlots = useMemo(() => {
    if (!selectedTeamMember) return [];

    const bookedStaffDetails = staffDetails.find(
      (d) => d.id === selectedTeamMember.id,
    );
    return (bookedStaffDetails?.busySlots || []).map((s) => ({
      fromTime: s.startTime,
      toTime: s.endTime,
      timezone: s.timezone,
    }));
  }, [selectedTeamMember]);

  const {
    startDate,
    toDate,
    excludingDates,
    availableTimeSlotsForSelectedDate: availableTimeSlots,
  } = useAvailableSlots({
    selectedDateInViewingTimezone: selectedDate,
    availability: availability || [],
    creationTimezone: fieldTimeZone || undefined,
    duration: getDurationInMinutes(),
    blocked: staffBusySlots,
    viewingTimezone: selectedTimezone?.tzCode || localTimeZoneCode,
    minimumNoticeInMinutes: minimumNotice || undefined,
    bufferTimeInMinutes: bufferTime || undefined,
    numberOfDisplayDays: MAX_DAYS_TO_DISPLAY,
    deps: [
      fieldValues,
      selectedDate,
      selectedTimezone,
      staffBusySlots,
      minimumNotice,
      bufferTime,
    ],
  });

  const submit = handleSubmit(async (values) => {
    const selectedTzCode = values.timezone?.tzCode || localTimeZoneCode;
    const selectedStartTime = values.selectedDate
      ? parseISO(values.selectedDate)
      : undefined;
    if (selectedStartTime && values.selectedTime) {
      selectedStartTime.setHours(values.selectedTime.hour);
      selectedStartTime.setMinutes(values.selectedTime.minute);
      selectedStartTime.setSeconds(0);
    }
    const selectedEndTime = selectedStartTime
      ? addMinutes(selectedStartTime, getDurationInMinutes())
      : undefined;
    const isValid =
      values.selectedTeamMember && values.selectedDate && values.selectedTime;

    const appointment = {
      bookedStaff: values.selectedTeamMember
        ? [
            {
              id: values.selectedTeamMember.id,
              firstName: values.selectedTeamMember.firstName,
              middleName: values.selectedTeamMember.middleName || undefined,
              lastName: values.selectedTeamMember.lastName,
            },
          ]
        : undefined,
      startTime: selectedStartTime
        ? fromZonedTime(selectedStartTime, selectedTzCode).toISOString()
        : undefined,
      endTime:
        selectedEndTime && isValid
          ? fromZonedTime(selectedEndTime, selectedTzCode).toISOString()
          : undefined,
      timezone: selectedTzCode,
      status: isValid ? "pending" : "invalid",
    };
    onChange(appointment);
    onBlur();
  });

  useEffect(() => {
    if (value === null) {
      setValue("selectedTeamMember", null);
      setValue("selectedDate", null);
      setValue("selectedTime", null);
    }
  }, [value]);

  return (
    <FieldWrapper disabled={disabled} isLoading={loading}>
      <AppointmentFieldWrapper
        id={id}
        error={error}
        errorId={errorId}
        field={fieldValues}
      >
        <FormProvider {...formMethods} key={selectedTimezone?.tzCode}>
          <div className="flex flex-col p-[1.6rem] tablet:p-[2.4rem] gap-[2.4rem]">
            <div className={rowStyles}>
              <div className="flex flex-1 flex-col gap-[0.4rem]">
                <Controller
                  control={control}
                  name="selectedTeamMember"
                  render={({ field, fieldState }) => (
                    <Select
                      id={field.name}
                      label="Select attendee"
                      title="Available attendees"
                      options={availableStaff.map((s) => ({
                        value: s,
                        label: [s.firstName, s.middleName, s.lastName]
                          .filter(Boolean)
                          .join(" "),
                      }))}
                      empty="No attendees available"
                      error={!!fieldState.error}
                      message={fieldState.error?.message}
                      value={field.value}
                      valueComparer={(o, v) => o?.id === v?.id}
                      onChange={async (v) => {
                        if (v?.id !== selectedTeamMember?.id) {
                          setValue("selectedDate", null);
                          setValue("selectedTime", null);
                          field.onChange(v);
                          await submit();
                        }
                      }}
                    />
                  )}
                />
              </div>
            </div>
            <div className={rowStyles}>
              <div
                className="flex flex-1 flex-col gap-medium"
                data-testid="info-container"
              >
                <div className={informationStyles}>
                  <Icon name="lightClock" />
                  <div className="text">
                    {getAppointmentDuration(duration || 0, durationUnit || "")}
                  </div>
                </div>
                <div className={informationStyles}>
                  <Icon name="lightMobile" />
                  <div className="text">
                    {intakeMeetingTypeLabel[meetingType || "inPerson"]}
                  </div>
                </div>
              </div>
              <div className="dropdown flex-1">
                <Controller
                  control={control}
                  name="timezone"
                  render={({ field, fieldState }) => (
                    <Select
                      id={field.name}
                      label="Timezone"
                      options={timeZoneOptionsByRegion.map((byRegion) => ({
                        heading: byRegion.region,
                        items: byRegion.items.map((tz) => ({
                          value: tz,
                          label: tz.label,
                        })),
                      }))}
                      empty="No options"
                      error={!!fieldState.error}
                      message={fieldState.error?.message}
                      valueComparer={(o, v) => o.tzCode === v.tzCode}
                      value={field.value}
                      onChange={async (v) => {
                        setValue("selectedDate", null);
                        setValue("selectedTime", null);
                        field.onChange(v);
                        await submit();
                      }}
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex flex-col border border-neutral-150 p-[1.6rem] tablet:p-[2.4rem] gap-medium rounded">
              <div className="flex flex-col tablet:flex-row w-full tablet:h-[36.4rem] justify-between gap-medium">
                <Controller
                  control={control}
                  name="selectedDate"
                  render={({ field }) => (
                    <div className="flex flex-1 justify-center">
                      <DatePicker
                        selectedDate={field.value}
                        startDate={startDate}
                        toDate={toDate}
                        excludingDates={excludingDates}
                        onChange={async (date) => {
                          field.onChange(date);
                          setValue("selectedTime", null);
                          await submit();
                        }}
                        disabled={
                          !selectedTeamMember?.id || !startDate || !toDate
                        }
                        viewingTimezone={selectedTimezone?.tzCode}
                      />
                    </div>
                  )}
                />
                <Controller
                  control={control}
                  name="selectedTime"
                  render={({ field }) => (
                    <TimePicker
                      heading="Time"
                      selectedTime={field.value}
                      availableTimes={availableTimeSlots}
                      onSelect={async (time) => {
                        if (
                          selectedTime &&
                          timeOfDayInMinutes(time) ===
                            timeOfDayInMinutes(selectedTime)
                        ) {
                          field.onChange(null);
                        } else {
                          field.onChange(time);
                        }
                        await submit();
                      }}
                    />
                  )}
                />
              </div>
              <div
                className={getSelectionInfoClassNames({
                  isSelected: !!selectedTime,
                })}
              >
                <div className="flex items-center text-ui gap-small">
                  {selectedTime ? (
                    <Icon name="solidCircleCheck" className="text-green-800" />
                  ) : (
                    <Icon name="solidCircleInfo" className="text-blue-660" />
                  )}
                  {selectedTime ? (
                    <span data-testid="selected-datetime">
                      You have selected{" "}
                      <b>
                        {selectedDate
                          ? format(parseISO(selectedDate), "dd MMM yyyy")
                          : ""}
                      </b>{" "}
                      at{" "}
                      <b>{selectedTime ? formatTimeOfDay(selectedTime) : ""}</b>{" "}
                      for your meeting
                    </span>
                  ) : (
                    <span>
                      A meeting <b>will not</b> be scheduled as you have not
                      selected a time
                    </span>
                  )}
                </div>
                {selectedTime && (
                  // eslint-disable-next-line jsx-a11y/anchor-is-valid
                  <Link
                    onClick={async () => {
                      setValue("selectedDate", null);
                      setValue("selectedTime", null);
                      await submit();
                    }}
                    variant="default"
                    text="Clear selection"
                    className="ml-[2.4rem] mt-[0.4rem]"
                    dataTestId="clear-selection"
                  />
                )}
              </div>
            </div>
          </div>
        </FormProvider>
      </AppointmentFieldWrapper>
      <ErrorDisplay uri={fieldValues.uri} index={index} error={error} />
    </FieldWrapper>
  );
};
